import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  Collapse,
  Tooltip,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import StorefrontIcon from '@mui/icons-material/Storefront';
import CalculateIcon from '@mui/icons-material/Calculate';
import HistoryIcon from '@mui/icons-material/History';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from '../../hooks/useTranslation';
import { tokens } from '../../theme';
import { formatCurrency, formatPercent, formatDateBR, reconcileInstallmentValues } from '../../utils';
import HistoricalSimulation from '../HistoricalSimulation';

const EMPTY_DATA = { valueInstallment: 0, totalPaid: 0, iofValue: 0, operationValue: 0 };

const SimulationResult = ({
  result,
  simulationPayload,
  isPaymentMethodFixed,
  installments,
  customDateInstallmentsCount,
  loadingSubmit,
  onSubmitByType,
  activeTab,
  setActiveTab,
  clientId,
  isRedutoOrigin,
  disableResale,
  needsResimulate,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();

  const reduto = { ...EMPTY_DATA, ...(result?.reduto ?? {}) };

  const resale = isRedutoOrigin ? EMPTY_DATA : { ...EMPTY_DATA, ...(result?.resale ?? {}) };

  const resaleInterestRate = isRedutoOrigin ? 0 : (simulationPayload?.interest_rate ?? 0);
  const resaleTacRate = isRedutoOrigin ? 0 : (simulationPayload?.tac_rate ?? 0);

  const isSimulation = activeTab === 'simulation';
  const redutoRates = result?.redutoRates ?? { interest_rate: 3.49, tac_rate: 1 };

  const [parcelasExpanded, setParcelasExpanded] = useState(false);

  useEffect(() => {
    if (needsResimulate) setParcelasExpanded(false);
  }, [needsResimulate]);

  const customDates = simulationPayload?.custom_dates ?? [];
  const resaleInstallments = reconcileInstallmentValues(customDates, resale.totalPaid);
  const redutoInstallments = reconcileInstallmentValues(customDates, reduto.totalPaid);

  const renderInstallmentColumn = (installments, testId, headerLabel, headerColor) => (
    <Box data-testid={testId} sx={{ flex: 1, px: 2 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: headerColor, mb: 1 }}>
        {headerLabel}
      </Typography>
      {installments.map((item, idx) => {
        const isLast = idx === installments.length - 1;
        return (
          <Box
            key={`${item.date}-${idx}`}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              py: 1,
              borderBottom: idx < installments.length - 1 ? '1px dashed #E5E7EB' : 'none',
            }}
          >
            <Typography sx={{ fontSize: 13, color: '#9CA3AF', width: 48 }}>
              {idx + 1}/{installments.length}
            </Typography>
            <Typography sx={{ fontSize: 14, color: '#2C2C2C', flex: 1 }}>
              {formatDateBR(item.date, false)}
            </Typography>
            <Typography sx={{ fontSize: 14, color: '#2C2C2C', fontWeight: 600 }}>
              R$ <Box component="span">{formatCurrency(item.value)}</Box>
            </Typography>
            {isLast && (
              <Tooltip title={t('simulation.installmentFeesTooltip')}>
                <InfoOutlinedIcon
                  aria-label={t('simulation.installmentFeesTooltip')}
                  sx={{ fontSize: 16, color: '#9CA3AF', ml: 1 }}
                />
              </Tooltip>
            )}
          </Box>
        );
      })}
    </Box>
  );

  return (
    <Box>
      {/* ── re-simulação obrigatória ───────────────────────────── */}
      {needsResimulate && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 2,
            mb: 2,
            borderRadius: 2,
            bgcolor: '#FEF3C7',
            border: '1px solid #F59E0B',
          }}
        >
          <WarningAmberIcon sx={{ color: '#D97706' }} />
          <Typography variant="body2" sx={{ color: '#92400E', fontWeight: 600 }}>
            {t('simulation.dataChanged')}
          </Typography>
        </Box>
      )}

      {/* ── Card unificado com abas ──────────────────────────────────────── */}
      <Paper sx={{ borderRadius: 2, border: 1, borderColor: '#E5E7EB', overflow: 'hidden' }}>
        {/* ── Abas ─────────────────────────────────────────────────────── */}
        <Box sx={{ display: 'flex', borderBottom: 1, borderColor: '#E5E7EB' }}>
          {[
            { value: 'simulation', label: t('simulation.simulator'), icon: <CalculateIcon /> },
            { value: 'historical', label: t('simulation.historical'), icon: <HistoryIcon /> },
          ].map(({ value, label, icon }) => {
            const selected = activeTab === value;
            return (
              <Button
                key={value}
                startIcon={icon}
                onClick={() => setActiveTab(value)}
                disableRipple
                sx={{
                  px: 3,
                  py: 1.5,
                  borderRadius: 0,
                  textTransform: 'none',
                  fontWeight: 600,
                  color: selected ? colors.greenAccent[500] : '#6B7280',
                  borderBottom: '2px solid',
                  borderColor: selected ? colors.greenAccent[500] : 'transparent',
                  bgcolor: 'transparent',
                  '&:hover': { bgcolor: '#F9FAFB' },
                }}
              >
                {label}
              </Button>
            );
          })}
        </Box>

        {/* ── Conteúdo ─────────────────────────────────────────────────── */}
        <Box sx={{ p: 3 }}>
          {isSimulation ? (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <AccountBalanceIcon sx={{ color: '#6B7280', mr: 1 }} />
            <Typography variant="h6" sx={{ color: '#2C2C2C', fontWeight: 600 }}>
              {t('simulation.financingComparison')}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: '#6B7280', mb: 3 }}>
            {t('simulation.compareTheConditions')}
          </Typography>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#FAFAFA' }}>
                  <TableCell
                    sx={{
                      color: '#6B7280',
                      fontWeight: 600,
                      borderBottom: 1,
                      borderColor: '#E5E7EB',
                    }}
                  >
                    {t('simulation.condition')}
                  </TableCell>
                  <TableCell align="center" sx={{ borderBottom: 1, borderColor: '#E5E7EB' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <StorefrontIcon sx={{ color: '#3C83F6', mr: 1, fontSize: 20 }} />
                      <Typography sx={{ color: '#3C83F6', fontWeight: 700 }}>
                        {t('simulation.yourReseller')}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ borderBottom: 1, borderColor: '#E5E7EB' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <AccountBalanceIcon sx={{ color: '#22C55E', mr: 1, fontSize: 20 }} />
                      <Typography sx={{ color: '#22C55E', fontWeight: 700 }}>
                        Reduto Capital
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {/* Juros */}
                <TableRow>
                  <TableCell sx={{ color: '#6B7280', borderBottom: 1, borderColor: '#E5E7EB' }}>
                    {t('simulation.interestRate')} (a.m.)
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      color: '#2C2C2C',
                      fontWeight: 600,
                      borderBottom: 1,
                      borderColor: '#E5E7EB',
                    }}
                  >
                    {formatPercent(resaleInterestRate) || '0'}%
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      color: '#2C2C2C',
                      fontWeight: 600,
                      borderBottom: 1,
                      borderColor: '#E5E7EB',
                    }}
                  >
                    {formatPercent(redutoRates.interest_rate)}%
                  </TableCell>
                </TableRow>

                {/* IOF */}
                <TableRow>
                  <TableCell sx={{ color: '#6B7280', borderBottom: 1, borderColor: '#E5E7EB' }}>
                    IOF
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      color: '#2C2C2C',
                      fontWeight: 600,
                      borderBottom: 1,
                      borderColor: '#E5E7EB',
                    }}
                  >
                    {isRedutoOrigin ? '0' : formatPercent(resale.iofValue) || '0'}%
                  </TableCell>
                  <TableCell align="center" sx={{ borderBottom: 1, borderColor: '#E5E7EB' }}>
                    {result?.redutoRates?.iof === false ? (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 0.5,
                        }}
                      >
                        <WarningAmberIcon sx={{ fontSize: 16, color: '#F59E0B' }} />
                        <Typography sx={{ color: '#92400E', fontWeight: 600, fontSize: '0.8rem' }}>
                          {t('simulation.noIof')}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography sx={{ color: '#2C2C2C', fontWeight: 600 }}>
                        {formatPercent(reduto.iofValue) || '0'}%
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>

                {/* TAC */}
                <TableRow>
                  <TableCell sx={{ color: '#6B7280', borderBottom: 1, borderColor: '#E5E7EB' }}>
                    TAC
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      color: '#2C2C2C',
                      fontWeight: 600,
                      borderBottom: 1,
                      borderColor: '#E5E7EB',
                    }}
                  >
                    {formatPercent(resaleTacRate) || '0'}%
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      color: '#2C2C2C',
                      fontWeight: 600,
                      borderBottom: 1,
                      borderColor: '#E5E7EB',
                    }}
                  >
                    {redutoRates.tac_rate}%
                  </TableCell>
                </TableRow>

                {/* Operacional */}
                <TableRow>
                  <TableCell sx={{ color: '#6B7280', borderBottom: 1, borderColor: '#E5E7EB' }}>
                    Operacional
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      color: '#2C2C2C',
                      fontWeight: 600,
                      borderBottom: 1,
                      borderColor: '#E5E7EB',
                    }}
                  >
                    R$ {formatCurrency(resale.operationValue) || '0,00'}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      color: '#2C2C2C',
                      fontWeight: 600,
                      borderBottom: 1,
                      borderColor: '#E5E7EB',
                    }}
                  >
                    R$ {formatCurrency(reduto.operationValue) || '0,00'}
                  </TableCell>
                </TableRow>

                {/* Parcelas — método fixo */}
                {isPaymentMethodFixed && (
                  <>
                    <TableRow>
                      <TableCell sx={{ color: '#6B7280', borderBottom: 1, borderColor: '#E5E7EB' }}>
                        {t('simulation.installments')}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          color: '#2C2C2C',
                          fontWeight: 600,
                          borderBottom: 1,
                          borderColor: '#E5E7EB',
                        }}
                      >
                        {installments}x
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          color: '#2C2C2C',
                          fontWeight: 600,
                          borderBottom: 1,
                          borderColor: '#E5E7EB',
                        }}
                      >
                        {installments}x
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell
                        sx={{
                          color: '#6B7280',
                          fontWeight: 600,
                          borderBottom: 1,
                          borderColor: '#E5E7EB',
                        }}
                      >
                        {t('simulation.ValueInstallment')}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          color: '#3C83F6',
                          fontWeight: 700,
                          fontSize: '1.125rem',
                          borderBottom: 1,
                          borderColor: '#E5E7EB',
                        }}
                      >
                        R${' '}
                        {resale.valueInstallment > 0
                          ? formatCurrency(resale.valueInstallment)
                          : '00,00'}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          color: '#22C55E',
                          fontWeight: 700,
                          fontSize: '1.125rem',
                          borderBottom: 1,
                          borderColor: '#E5E7EB',
                        }}
                      >
                        R${' '}
                        {reduto.valueInstallment > 0
                          ? formatCurrency(reduto.valueInstallment)
                          : '00,00'}
                      </TableCell>
                    </TableRow>
                  </>
                )}

                {/* Parcelas — método customizado */}
                {!isPaymentMethodFixed && customDateInstallmentsCount > 0 && (
                  <>
                    <TableRow>
                      <TableCell sx={{ color: '#6B7280', borderBottom: 1, borderColor: '#E5E7EB' }}>
                        {t('simulation.installments')}
                        <Typography
                          component="button"
                          type="button"
                          onClick={() => setParcelasExpanded((prev) => !prev)}
                          aria-expanded={parcelasExpanded}
                          sx={{
                            background: 'none',
                            border: 0,
                            padding: 0,
                            font: 'inherit',
                            ml: 1,
                            fontSize: 13,
                            color: '#3C83F6',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                          }}
                        >
                          {parcelasExpanded
                            ? `▴ ${t('simulation.hideInstallments')}`
                            : `▾ ${t('simulation.viewInstallments')}`}
                        </Typography>
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          color: '#2C2C2C',
                          fontWeight: 600,
                          borderBottom: 1,
                          borderColor: '#E5E7EB',
                        }}
                      >
                        {customDateInstallmentsCount}x
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          color: '#2C2C2C',
                          fontWeight: 600,
                          borderBottom: 1,
                          borderColor: '#E5E7EB',
                        }}
                      >
                        {customDateInstallmentsCount}x
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell colSpan={3} sx={{ p: 0, borderBottom: parcelasExpanded ? 1 : 0, borderColor: '#E5E7EB' }}>
                        <Collapse in={parcelasExpanded} unmountOnExit>
                          <Box data-testid="parcelas-expandidas" sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex' }}>
                              {renderInstallmentColumn(
                                resaleInstallments,
                                'parcelas-coluna-revenda',
                                t('simulation.yourReseller'),
                                '#3C83F6'
                              )}
                              <Box sx={{ borderLeft: '1px solid #E5E7EB' }} />
                              {renderInstallmentColumn(
                                redutoInstallments,
                                'parcelas-coluna-reduto',
                                'Reduto Capital',
                                '#22C55E'
                              )}
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </>
                )}

                {/* Total pago */}
                <TableRow>
                  <TableCell sx={{ color: '#6B7280', fontWeight: 600 }}>
                    {t('simulation.totalPaid')}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ color: '#3C83F6', fontWeight: 700, fontSize: '1.125rem' }}
                  >
                    R$ {resale.totalPaid > 0 ? formatCurrency(resale.totalPaid) : '00,00'}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ color: '#22C55E', fontWeight: 700, fontSize: '1.125rem' }}
                  >
                    R$ {reduto.totalPaid > 0 ? formatCurrency(reduto.totalPaid) : '00,00'}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<StorefrontIcon />}
                disabled={loadingSubmit || disableResale || needsResimulate}
                onClick={() => onSubmitByType('REVENDA')}
                sx={{
                  bgcolor: '#3C83F6',
                  color: '#FFFFFF',
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 700,
                  '&:hover': { bgcolor: '#2563EB' },
                  '&:disabled': { bgcolor: '#D1D5DB', color: '#9CA3AF' },
                }}
              >
                {loadingSubmit
                  ? t('simulation.sending')
                  : t('simulation.loanThroughResale').toUpperCase()}
              </Button>
            </Grid>
            <Grid item xs={12} md={6}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<AccountBalanceIcon />}
                disabled={loadingSubmit || needsResimulate}
                onClick={() => onSubmitByType('REDUTO_CAPITAL')}
                sx={{
                  bgcolor: colors.greenAccent[500],
                  color: '#2C2C2C',
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 700,
                  '&:hover': { bgcolor: '#73C43D' },
                  '&:disabled': { bgcolor: '#D1D5DB', color: '#9CA3AF' },
                }}
              >
                {loadingSubmit
                  ? t('simulation.sending')
                  : `${t('simulation.requestBy').toUpperCase()} REDUTO CAPITAL`}
              </Button>
            </Grid>
          </Grid>
            </>
          ) : (
            <HistoricalSimulation client_id={clientId} bare />
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default SimulationResult;
